import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { acBase, Spinner, fmtDate, ADMIN_WA } from './academyUtils';
import { T, Card, Btn, MonoLabel, Field, Alert } from '../ui';

// ── Currency ────────────────────────────────────────────────────────────────
const RATES = { AED: 1, EGP: 14.5, USD: 0.272 };
const CCY_SYMBOLS = { AED: 'AED', EGP: 'EGP', USD: '$' };
function cvt(amount, from, to) {
  if (!amount || from === to) return amount || 0;
  return (amount / RATES[from]) * RATES[to];
}
function fmt(amount, ccy) {
  return `${CCY_SYMBOLS[ccy] || ccy} ${Number(amount || 0).toFixed(2)}`;
}

// ── Payroll logic ────────────────────────────────────────────────────────────
function calcPayroll(cfg, hoursCount, adjustments = []) {
  if (!cfg) return { base: 0, bonus: 0, deductions: 0, total: 0 };
  let base = 0, bonus = 0;
  if (cfg.payment_type === 'hourly') {
    base = hoursCount * cfg.base_rate;
    if (hoursCount > cfg.threshold_hours && cfg.threshold_hours > 0) {
      bonus = (hoursCount - cfg.threshold_hours) * cfg.bonus_rate;
    }
  } else if (cfg.payment_type === 'fixed') {
    base = hoursCount >= (cfg.min_hours_required || 0) ? cfg.fixed_amount : (cfg.fixed_amount * hoursCount / (cfg.min_hours_required || 1));
  } else if (cfg.payment_type === 'percentage') {
    base = hoursCount * cfg.base_rate * (cfg.percentage / 100);
  }
  const adjBonus     = adjustments.filter(a => a.adjustment_type === 'bonus').reduce((s,a) => s + Number(a.amount||0), 0);
  const adjDeduction = adjustments.filter(a => a.adjustment_type === 'deduction').reduce((s,a) => s + Number(a.amount||0), 0);
  const total = base + bonus + adjBonus - adjDeduction;
  return { base, bonus: bonus + adjBonus, deductions: adjDeduction, total };
}

// ── Colours per session status ───────────────────────────────────────────────
const SESSION_COLOR = {
  taught:                           { bg:'#e8f5ee', color:'#1a7a4a', border:'#c8ead8',   label:'Taught' },
  cancelled_by_student_excused:     { bg:'#f7f9fc', color:'#5a6a88', border:'#dde3ed',   label:'Excused' },
  cancelled_by_student_charged:     { bg:'#fff8e8', color:'#b8620a', border:'#f0d890',   label:'Charged (½)' },
  cancelled_by_teacher:             { bg:'#fff5f5', color:'#c0392b', border:'rgba(192,57,43,0.2)', label:'Teacher Cancel' },
  makeup:                           { bg:'#f0f6ff', color:'#1a5fa8', border:'#c0d8f8',   label:'Make-up' },
  manual_adjustment:                { bg:'#f5ebff', color:'#5a1a8a', border:'#e0c8f8',   label:'Adjustment' },
};

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const PAY_TYPE_COLORS = { hourly:'#1a5fa8', fixed:'#1a7a4a', percentage:'#b8620a' };

const inp = { ...acBase.inp, marginBottom:0 };

function SectionHeader({ title, action }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, flexWrap:'wrap', gap:8 }}>
      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:700, color:'#1a2a4a' }}>{title}</div>
      {action}
    </div>
  );
}

function StatusPill({ status }) {
  const s = { pending:{bg:'#fff8e8',color:'#b8620a',border:'#f0d890'}, approved:{bg:'#e8f0fb',color:'#1a5fa8',border:'#c0d8f8'}, paid:{bg:'#e8f5ee',color:'#1a7a4a',border:'#c8ead8'} }[status] || {};
  return <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:100, background:s.bg, color:s.color, border:`1px solid ${s.border}`, fontFamily:"'JetBrains Mono',monospace" }}>{status}</span>;
}

// ── Main component ───────────────────────────────────────────────────────────
export default function PayrollAdmin() {
  const now = new Date();
  const [section,    setSection]    = useState('overview');
  const [selMonth,   setSelMonth]   = useState(now.getMonth() + 1);
  const [selYear,    setSelYear]    = useState(now.getFullYear());
  const [viewCcy,    setViewCcy]    = useState('AED');
  const [teachers,   setTeachers]   = useState([]);
  const [payrolls,   setPayrolls]   = useState([]);
  const [configs,    setConfigs]    = useState([]);
  const [hoursLog,   setHoursLog]   = useState([]);
  const [adjustments,setAdjustments]= useState([]);
  const [policies,   setPolicies]   = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [savingId,   setSavingId]   = useState(null);
  const [toast,      setToast]      = useState('');

  // Filters
  const [logTeacher, setLogTeacher] = useState('all');
  const [logStatus,  setLogStatus]  = useState('all');
  const [adjTeacher, setAdjTeacher] = useState('');
  const [adjType,    setAdjType]    = useState('bonus');
  const [adjAmount,  setAdjAmount]  = useState('');
  const [adjHours,   setAdjHours]   = useState('');
  const [adjReason,  setAdjReason]  = useState('');
  const [adjMonth,   setAdjMonth]   = useState(now.getMonth() + 1);
  const [adjYear,    setAdjYear]    = useState(now.getFullYear());

  // Config edit state
  const [editCfgId,  setEditCfgId]  = useState(null);
  const [cfgForm,    setCfgForm]    = useState({});

  // Hours log edit
  const [editLogId,  setEditLogId]  = useState(null);
  const [editLog,    setEditLog]    = useState({});

  // Policy edit
  const [editPolicyId, setEditPolicyId] = useState(null);
  const [policyForm,   setPolicyForm]   = useState({});

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(''), 3500); }

  const fetchTeachers = useCallback(async () => {
    const { data } = await supabase.from('teachers').select('id,name,active').eq('active',true).order('name');
    setTeachers(data || []);
  }, []);

  const fetchPayrolls = useCallback(async () => {
    const { data } = await supabase.from('teacher_payroll')
      .select('*').eq('month', selMonth).eq('year', selYear);
    setPayrolls(data || []);
  }, [selMonth, selYear]);

  const fetchConfigs = useCallback(async () => {
    const { data } = await supabase.from('teacher_payment_config').select('*');
    setConfigs(data || []);
  }, []);

  const fetchHoursLog = useCallback(async () => {
    setLoading(true);
    const startDate = `${selYear}-${String(selMonth).padStart(2,'0')}-01`;
    const endDate   = new Date(selYear, selMonth, 0).toISOString().slice(0,10);
    const q = supabase.from('teacher_hours_log')
      .select('*, teachers(name), students(name)')
      .gte('session_date', startDate)
      .lte('session_date', endDate)
      .order('session_date', { ascending: false });
    const { data } = await q;
    setHoursLog(data || []);
    setLoading(false);
  }, [selMonth, selYear]);

  const fetchAdjustments = useCallback(async () => {
    const { data } = await supabase.from('teacher_adjustments')
      .select('*, teachers(name)').order('created_at', { ascending: false }).limit(100);
    setAdjustments(data || []);
  }, []);

  const fetchPolicies = useCallback(async () => {
    const { data } = await supabase.from('cancellation_policy').select('*').order('sort_order');
    setPolicies(data || []);
  }, []);

  useEffect(() => {
    fetchTeachers();
    fetchConfigs();
    fetchAdjustments();
    fetchPolicies();
  }, [fetchTeachers, fetchConfigs, fetchAdjustments, fetchPolicies]);

  useEffect(() => {
    fetchPayrolls();
    fetchHoursLog();
  }, [fetchPayrolls, fetchHoursLog]);

  // ── Helpers ─────────────────────────────────────────────────────────────
  function cfgFor(teacherId) { return configs.find(c => c.teacher_id === teacherId); }
  function payrollFor(teacherId) { return payrolls.find(p => p.teacher_id === teacherId); }
  function adjFor(payrollId) { return adjustments.filter(a => a.payroll_id === payrollId); }

  function teacherHoursThisMonth(teacherId) {
    const entries = hoursLog.filter(h => h.teacher_id === teacherId);
    const taught  = entries.filter(h => ['taught','makeup','cancelled_by_student_charged'].includes(h.session_status))
                           .reduce((s,h) => s + Number(h.hours_counted||0), 0);
    return { taught: entries.reduce((s,h) => s + Number(h.scheduled_duration||0), 0), counted: taught };
  }

  // ── Payroll upsert ───────────────────────────────────────────────────────
  async function recalcAndSavePayroll(teacherId) {
    const cfg  = cfgFor(teacherId);
    const { counted } = teacherHoursThisMonth(teacherId);
    const existing = payrollFor(teacherId);
    const adjs = existing ? adjFor(existing.id) : [];
    const { base, bonus, deductions, total } = calcPayroll(cfg, counted, adjs);
    const record = {
      teacher_id: teacherId, month: selMonth, year: selYear,
      total_hours_taught: teacherHoursThisMonth(teacherId).taught,
      total_hours_counted: counted,
      base_amount: base, bonus_amount: bonus, deduction_amount: deductions, total_amount: total,
      currency: cfg?.currency || 'AED',
    };
    await supabase.from('teacher_payroll').upsert(record, { onConflict: 'teacher_id,month,year' });
    fetchPayrolls();
  }

  async function updatePayrollStatus(payrollId, status) {
    setSavingId(payrollId);
    const upd = { status };
    if (status === 'paid') upd.paid_date = new Date().toISOString().slice(0,10);
    await supabase.from('teacher_payroll').update(upd).eq('id', payrollId);
    await fetchPayrolls();
    setSavingId(null);
    showToast(status === 'paid' ? '✓ Marked as paid' : '✓ Status updated');
  }

  // ── Config save ──────────────────────────────────────────────────────────
  async function saveConfig(teacherId) {
    setSavingId(teacherId);
    const existing = cfgFor(teacherId);
    const row = { teacher_id: teacherId, ...cfgForm };
    if (existing) {
      await supabase.from('teacher_payment_config').update(row).eq('id', existing.id);
    } else {
      await supabase.from('teacher_payment_config').insert(row);
    }
    await fetchConfigs();
    await recalcAndSavePayroll(teacherId);
    setEditCfgId(null); setSavingId(null);
    showToast('✓ Payment config saved');
  }

  // ── Hours log edit ───────────────────────────────────────────────────────
  async function saveLogEntry(id) {
    await supabase.from('teacher_hours_log').update(editLog).eq('id', id);
    const entry = hoursLog.find(h => h.id === id);
    if (entry) await recalcAndSavePayroll(entry.teacher_id);
    setEditLogId(null);
    fetchHoursLog();
    showToast('✓ Session updated');
  }

  async function moveToNextMonth(entry) {
    const nextDate = new Date(entry.session_date);
    nextDate.setMonth(nextDate.getMonth() + 1);
    await supabase.from('teacher_hours_log').update({ moved_to_next_month: true, session_date: nextDate.toISOString().slice(0,10) }).eq('id', entry.id);
    fetchHoursLog();
    showToast('✓ Moved to next month');
  }

  // ── Adjustment save ──────────────────────────────────────────────────────
  async function saveAdjustment() {
    if (!adjTeacher || !adjReason) return;
    let payrollRow = payrollFor(adjTeacher);
    if (!payrollRow) {
      const { data } = await supabase.from('teacher_payroll').upsert({
        teacher_id: adjTeacher, month: adjMonth, year: adjYear, status: 'pending',
        currency: cfgFor(adjTeacher)?.currency || 'AED',
      }, { onConflict: 'teacher_id,month,year' }).select().single();
      payrollRow = data;
    }
    await supabase.from('teacher_adjustments').insert({
      teacher_id: adjTeacher, payroll_id: payrollRow?.id,
      adjustment_type: adjType, amount: Number(adjAmount)||0,
      hours: Number(adjHours)||0, reason: adjReason, created_by: 'admin',
    });
    await recalcAndSavePayroll(adjTeacher);
    await fetchAdjustments();
    setAdjTeacher(''); setAdjAmount(''); setAdjHours(''); setAdjReason('');
    showToast('✓ Adjustment saved');
  }

  // ── Policy save ──────────────────────────────────────────────────────────
  async function savePolicy(id) {
    await supabase.from('cancellation_policy').update(policyForm).eq('id', id);
    setEditPolicyId(null);
    fetchPolicies();
    showToast('✓ Policy updated');
  }

  function copyPolicy() {
    const lines = policies.filter(p => p.active).map(p => `*${p.rule_title}*\n${p.rule_description}`).join('\n\n');
    const text = `📋 COMPASS ACADEMY — CANCELLATION POLICY\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n${lines}\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\nFor queries contact Mr. Ahmed Salem\nwa.me/${ADMIN_WA}`;
    navigator.clipboard?.writeText(text).then(() => showToast('✓ Policy copied to clipboard'));
  }

  // ── CSV export ───────────────────────────────────────────────────────────
  function exportCSV() {
    const headers = ['Teacher','Payment Type','Hours Taught','Hours Counted','Base','Bonus','Deductions','Total','Currency','Status'];
    const rows = teachers.map(t => {
      const p   = payrollFor(t.id);
      const cfg = cfgFor(t.id);
      if (!p && !cfg) return null;
      const { counted } = teacherHoursThisMonth(t.id);
      const adjs = p ? adjFor(p.id) : [];
      const { base, bonus, deductions, total } = calcPayroll(cfg, counted, adjs);
      return [t.name, cfg?.payment_type||'—', teacherHoursThisMonth(t.id).taught.toFixed(2), counted.toFixed(2),
        base.toFixed(2), bonus.toFixed(2), deductions.toFixed(2), total.toFixed(2),
        cfg?.currency||'AED', p?.status||'pending'];
    }).filter(Boolean);
    const csv = [headers, ...rows].map(r => r.map(x => `"${x}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type:'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a'); a.href = url;
    a.download = `payroll_${MONTHS[selMonth-1]}_${selYear}.csv`;
    a.click(); URL.revokeObjectURL(url);
  }

  // ── Summary stats ────────────────────────────────────────────────────────
  const totalDue  = teachers.reduce((s,t) => {
    const cfg = cfgFor(t.id);
    const { counted } = teacherHoursThisMonth(t.id);
    const p = payrollFor(t.id);
    const { total } = calcPayroll(cfg, counted, p ? adjFor(p.id) : []);
    return s + cvt(total, cfg?.currency||'AED', viewCcy);
  }, 0);
  const paidCount    = payrolls.filter(p => p.status === 'paid').length;
  const pendingCount = teachers.length - paidCount;

  const SECTIONS = [
    ['overview',   '📊 Monthly Overview'],
    ['config',     '⚙️ Payment Config'],
    ['hours',      '🕐 Hours Log'],
    ['adjustments','⚖️ Adjustments'],
    ['policy',     '📋 Policy'],
  ];

  return (
    <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif" }}>

      {/* Toast */}
      {toast && (
        <div style={{ position:'fixed', top:20, right:20, zIndex:2000, background: toast.startsWith('✓')?'#1a7a4a':'#c0392b', color:'#fff', padding:'11px 18px', borderRadius:9, fontSize:13.5, fontWeight:600, boxShadow:'0 4px 16px rgba(0,0,0,0.15)' }}>
          {toast}
        </div>
      )}

      {/* Print styles */}
      <style>{`@media print { .no-print { display:none!important; } .print-show { display:block!important; } }`}</style>

      {/* Section nav */}
      <div className="no-print" style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:16 }}>
        {SECTIONS.map(([k,l]) => (
          <button key={k} onClick={() => setSection(k)}
            style={{ padding:'7px 14px', borderRadius:7, border:`1px solid ${section===k?T.gold:T.border}`, background:section===k?T.gold+'22':'transparent', color:section===k?T.gold:T.muted, cursor:'pointer', fontSize:12.5, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
            {l}
          </button>
        ))}
      </div>

      {/* ── SECTION A: MONTHLY OVERVIEW ── */}
      {section === 'overview' && (
        <div>
          {/* Controls */}
          <div className="no-print" style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap', marginBottom:16 }}>
            <div style={{ display:'flex', gap:6, alignItems:'center' }}>
              <select value={selMonth} onChange={e=>setSelMonth(Number(e.target.value))}
                style={{ ...inp, width:130 }}>
                {MONTHS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
              </select>
              <input type="number" value={selYear} onChange={e=>setSelYear(Number(e.target.value))}
                style={{ ...inp, width:80 }}/>
            </div>
            <div style={{ display:'flex', gap:4 }}>
              {['AED','EGP','USD'].map(c => (
                <button key={c} onClick={() => setViewCcy(c)}
                  style={{ padding:'6px 12px', borderRadius:6, border:`1.5px solid ${viewCcy===c?T.gold:T.border}`, background:viewCcy===c?T.gold+'22':'transparent', color:viewCcy===c?T.gold:T.muted, cursor:'pointer', fontSize:12, fontWeight:viewCcy===c?700:400 }}>
                  {c}
                </button>
              ))}
            </div>
            <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
              <Btn small onClick={exportCSV}>📥 Export CSV</Btn>
              <Btn small onClick={() => window.print()}>🖨️ Print Report</Btn>
              <Btn small variant="primary" onClick={async()=>{ for(const t of teachers) await recalcAndSavePayroll(t.id); showToast('✓ Payroll recalculated'); }}>
                ↻ Recalculate All
              </Btn>
            </div>
          </div>

          {/* Stats row */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:18 }}>
            {[
              ['Total Teachers', teachers.length, T.navy||'#1a2a4a'],
              [`Total Due (${viewCcy})`, fmt(totalDue, viewCcy), '#b8620a'],
              ['Paid', paidCount, '#1a7a4a'],
              ['Pending', pendingCount, pendingCount>0?'#b8620a':'#9aa3b8'],
            ].map(([lbl,val,col]) => (
              <div key={lbl} style={{ background:'#f7f9fc', border:`1px solid ${col}33`, borderRadius:9, padding:'12px 16px', textAlign:'center' }}>
                <div style={{ fontSize:22, fontWeight:700, color:col, fontFamily:"'Cormorant Garamond',serif" }}>{val}</div>
                <div style={{ fontSize:11.5, color:'#5a6a88', marginTop:2 }}>{lbl}</div>
              </div>
            ))}
          </div>

          {/* Payroll table */}
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:'#f7f9fc' }}>
                  {['Teacher','Type','Hrs Taught','Hrs Counted','Base','Bonus/Adj','Deductions',`Total (${viewCcy})`,'Status','Actions'].map(h=>(
                    <th key={h} style={{ padding:'9px 12px', textAlign:'left', fontFamily:"'JetBrains Mono',monospace", fontSize:10, letterSpacing:'.1em', textTransform:'uppercase', color:'#9aa3b8', borderBottom:'1px solid #dde3ed', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {teachers.map(t => {
                  const cfg  = cfgFor(t.id);
                  const p    = payrollFor(t.id);
                  const { taught, counted } = teacherHoursThisMonth(t.id);
                  const adjs = p ? adjFor(p.id) : [];
                  const { base, bonus, deductions, total } = calcPayroll(cfg, counted, adjs);
                  const displayTotal = cvt(total, cfg?.currency||'AED', viewCcy);
                  return (
                    <tr key={t.id} style={{ borderBottom:'1px solid #f0f4f8' }}
                      onMouseEnter={e=>e.currentTarget.style.background='#f7f9fc'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <td style={{ padding:'9px 12px', fontWeight:600, color:'#1a2233' }}>{t.name}</td>
                      <td style={{ padding:'9px 12px' }}>
                        {cfg ? (
                          <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:100, color:PAY_TYPE_COLORS[cfg.payment_type]||'#5a6a88', background:`${PAY_TYPE_COLORS[cfg.payment_type]}18`, border:`1px solid ${PAY_TYPE_COLORS[cfg.payment_type]}33` }}>
                            {cfg.payment_type}
                          </span>
                        ) : <span style={{ color:'#9aa3b8', fontSize:12 }}>—</span>}
                      </td>
                      <td style={{ padding:'9px 12px', color:'#1a2233' }}>{taught.toFixed(1)}</td>
                      <td style={{ padding:'9px 12px', color:'#1a2233', fontWeight:600 }}>{counted.toFixed(1)}</td>
                      <td style={{ padding:'9px 12px', color:'#5a6a88' }}>{fmt(cvt(base, cfg?.currency||'AED', viewCcy), viewCcy)}</td>
                      <td style={{ padding:'9px 12px', color: bonus>0?'#1a7a4a':'#5a6a88' }}>{fmt(cvt(bonus, cfg?.currency||'AED', viewCcy), viewCcy)}</td>
                      <td style={{ padding:'9px 12px', color: deductions>0?'#c0392b':'#5a6a88' }}>{deductions>0 ? `-${fmt(cvt(deductions, cfg?.currency||'AED', viewCcy), viewCcy)}` : '—'}</td>
                      <td style={{ padding:'9px 12px', fontWeight:700, color: total>0?'#1a2233':'#9aa3b8' }}>{fmt(displayTotal, viewCcy)}</td>
                      <td style={{ padding:'9px 12px' }}><StatusPill status={p?.status||'pending'}/></td>
                      <td style={{ padding:'9px 6px' }}>
                        <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                          {(!p || p.status === 'pending') && (
                            <Btn small onClick={async()=>{ await recalcAndSavePayroll(t.id); const px=payrollFor(t.id); if(px) await updatePayrollStatus(px.id,'approved'); }} disabled={savingId===t.id}>Approve</Btn>
                          )}
                          {p?.status === 'approved' && (
                            <Btn small variant="primary" onClick={()=>updatePayrollStatus(p.id,'paid')} disabled={savingId===p.id}>Mark Paid</Btn>
                          )}
                          {p?.status === 'paid' && (
                            <span style={{ fontSize:11, color:'#1a7a4a' }}>✓ {p.paid_date || ''}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── SECTION B: PAYMENT CONFIG ── */}
      {section === 'config' && (
        <div>
          <SectionHeader title="Payment Configuration"/>
          <Alert type="info">Set each teacher's payment type and rate. Changes are applied to the current month's payroll calculation when saved.</Alert>

          {teachers.map(t => {
            const cfg = cfgFor(t.id);
            const isEditing = editCfgId === t.id;
            return (
              <Card key={t.id} style={{ marginBottom:10, padding:'14px 18px' }}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:12, flexWrap:'wrap' }}>
                  <div style={{ flex:1, minWidth:160 }}>
                    <div style={{ fontWeight:600, fontSize:14, color:'#1a2233', marginBottom:3 }}>{t.name}</div>
                    {cfg ? (
                      <div style={{ fontSize:12.5, color:'#5a6a88' }}>
                        {cfg.payment_type === 'hourly' && `${cfg.base_rate} ${cfg.currency}/hr · Bonus: ${cfg.bonus_rate} ${cfg.currency}/hr after ${cfg.threshold_hours}h`}
                        {cfg.payment_type === 'fixed'  && `Fixed: ${cfg.fixed_amount} ${cfg.currency} (min ${cfg.min_hours_required}h)`}
                        {cfg.payment_type === 'percentage' && `${cfg.percentage}% of ${cfg.base_rate} ${cfg.currency}/hr`}
                      </div>
                    ) : <div style={{ fontSize:12, color:'#c0392b', fontStyle:'italic' }}>No config — teacher won't appear in payroll</div>}
                  </div>
                  <Btn small onClick={() => {
                    setEditCfgId(isEditing ? null : t.id);
                    setCfgForm(cfg ? { payment_type:cfg.payment_type, currency:cfg.currency, base_rate:cfg.base_rate, threshold_hours:cfg.threshold_hours, bonus_rate:cfg.bonus_rate, fixed_amount:cfg.fixed_amount, percentage:cfg.percentage, min_hours_required:cfg.min_hours_required, notes:cfg.notes||'' }
                      : { payment_type:'hourly', currency:'AED', base_rate:0, threshold_hours:0, bonus_rate:0, fixed_amount:0, percentage:0, min_hours_required:0, notes:'' });
                  }}>
                    {isEditing ? 'Close' : cfg ? 'Edit' : '+ Configure'}
                  </Btn>
                </div>

                {isEditing && (
                  <div style={{ marginTop:14, paddingTop:14, borderTop:`1px solid ${T.border}` }}>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:10, marginBottom:12 }}>
                      <div>
                        <MonoLabel style={{ marginBottom:4 }}>Payment Type</MonoLabel>
                        <select value={cfgForm.payment_type} onChange={e=>setCfgForm(f=>({...f,payment_type:e.target.value}))} style={inp}>
                          <option value="hourly">Hourly</option>
                          <option value="fixed">Fixed Monthly</option>
                          <option value="percentage">Percentage</option>
                        </select>
                      </div>
                      <div>
                        <MonoLabel style={{ marginBottom:4 }}>Currency</MonoLabel>
                        <select value={cfgForm.currency} onChange={e=>setCfgForm(f=>({...f,currency:e.target.value}))} style={inp}>
                          <option value="AED">AED</option>
                          <option value="EGP">EGP</option>
                          <option value="USD">USD</option>
                        </select>
                      </div>

                      {cfgForm.payment_type === 'hourly' && <>
                        <div><MonoLabel style={{ marginBottom:4 }}>Base Rate / hr</MonoLabel>
                          <input type="number" value={cfgForm.base_rate} onChange={e=>setCfgForm(f=>({...f,base_rate:e.target.value}))} style={inp}/></div>
                        <div><MonoLabel style={{ marginBottom:4 }}>Bonus Threshold (hrs)</MonoLabel>
                          <input type="number" value={cfgForm.threshold_hours} onChange={e=>setCfgForm(f=>({...f,threshold_hours:e.target.value}))} style={inp}/></div>
                        <div><MonoLabel style={{ marginBottom:4 }}>Bonus Rate / hr (above threshold)</MonoLabel>
                          <input type="number" value={cfgForm.bonus_rate} onChange={e=>setCfgForm(f=>({...f,bonus_rate:e.target.value}))} style={inp}/></div>
                      </>}

                      {cfgForm.payment_type === 'fixed' && <>
                        <div><MonoLabel style={{ marginBottom:4 }}>Fixed Amount</MonoLabel>
                          <input type="number" value={cfgForm.fixed_amount} onChange={e=>setCfgForm(f=>({...f,fixed_amount:e.target.value}))} style={inp}/></div>
                        <div><MonoLabel style={{ marginBottom:4 }}>Min Hours Required</MonoLabel>
                          <input type="number" value={cfgForm.min_hours_required} onChange={e=>setCfgForm(f=>({...f,min_hours_required:e.target.value}))} style={inp}/></div>
                      </>}

                      {cfgForm.payment_type === 'percentage' && <>
                        <div><MonoLabel style={{ marginBottom:4 }}>Student Rate / hr</MonoLabel>
                          <input type="number" value={cfgForm.base_rate} onChange={e=>setCfgForm(f=>({...f,base_rate:e.target.value}))} style={inp}/></div>
                        <div><MonoLabel style={{ marginBottom:4 }}>Teacher % Share</MonoLabel>
                          <input type="number" value={cfgForm.percentage} onChange={e=>setCfgForm(f=>({...f,percentage:e.target.value}))} style={inp}/></div>
                      </>}

                      <div><MonoLabel style={{ marginBottom:4 }}>Notes</MonoLabel>
                        <input value={cfgForm.notes||''} onChange={e=>setCfgForm(f=>({...f,notes:e.target.value}))} placeholder="Optional" style={inp}/></div>
                    </div>
                    <Btn variant="primary" small onClick={() => saveConfig(t.id)} disabled={savingId===t.id}>
                      {savingId===t.id ? 'Saving…' : 'Save Config'}
                    </Btn>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* ── SECTION C: HOURS LOG ── */}
      {section === 'hours' && (
        <div>
          <SectionHeader title="Hours Log"/>
          {/* Filters */}
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:14 }}>
            <select value={selMonth} onChange={e=>setSelMonth(Number(e.target.value))} style={{ ...inp, width:120 }}>
              {MONTHS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
            </select>
            <input type="number" value={selYear} onChange={e=>setSelYear(Number(e.target.value))} style={{ ...inp, width:80 }}/>
            <select value={logTeacher} onChange={e=>setLogTeacher(e.target.value)} style={{ ...inp, width:180 }}>
              <option value="all">All Teachers</option>
              {teachers.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <select value={logStatus} onChange={e=>setLogStatus(e.target.value)} style={{ ...inp, width:160 }}>
              <option value="all">All Statuses</option>
              {Object.entries(SESSION_COLOR).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>

          {loading ? <div style={{ display:'flex', justifyContent:'center', padding:40 }}><Spinner/></div> : (
            <>
              {hoursLog
                .filter(h => (logTeacher==='all' || h.teacher_id===logTeacher) && (logStatus==='all' || h.session_status===logStatus))
                .map(h => {
                  const sc = SESSION_COLOR[h.session_status] || {};
                  const isEditing = editLogId === h.id;
                  return (
                    <Card key={h.id} style={{ marginBottom:8, padding:'12px 16px', borderLeft:`3px solid ${sc.color||'#9aa3b8'}` }}>
                      {!isEditing ? (
                        <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-start' }}>
                          <div style={{ flex:1, minWidth:180 }}>
                            <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:3, flexWrap:'wrap' }}>
                              <span style={{ fontWeight:600, fontSize:13, color:'#1a2233' }}>{h.teachers?.name||'—'}</span>
                              <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:100, background:sc.bg, color:sc.color, border:`1px solid ${sc.border}` }}>{sc.label}</span>
                              {h.admin_override && <span style={{ fontSize:10, color:'#5a1a8a', background:'#f5ebff', border:'1px solid #e0c8f8', borderRadius:100, padding:'1px 7px' }}>Override</span>}
                            </div>
                            <div style={{ fontSize:12, color:'#5a6a88' }}>
                              {fmtDate(h.session_date)} · {h.students?.name||'—'} · Sched: {Number(h.scheduled_duration).toFixed(1)}h · Counted: <strong style={{ color:'#1a2233' }}>{Number(h.hours_counted).toFixed(1)}h</strong>
                            </div>
                            {h.notes && <div style={{ fontSize:11.5, color:'#9aa3b8', fontStyle:'italic', marginTop:2 }}>{h.notes}</div>}
                          </div>
                          <div style={{ display:'flex', gap:5 }}>
                            <Btn small onClick={()=>{ setEditLogId(h.id); setEditLog({ hours_counted:h.hours_counted, session_status:h.session_status, notes:h.notes||'', admin_override:h.admin_override, admin_override_reason:h.admin_override_reason||'' }); }}>Edit</Btn>
                            {!h.moved_to_next_month && <Btn small onClick={()=>moveToNextMonth(h)}>→ Next Month</Btn>}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:8, marginBottom:10 }}>
                            <div>
                              <MonoLabel style={{ marginBottom:3 }}>Hours Counted</MonoLabel>
                              <input type="number" step="0.5" value={editLog.hours_counted} onChange={e=>setEditLog(x=>({...x,hours_counted:e.target.value}))} style={inp}/>
                            </div>
                            <div>
                              <MonoLabel style={{ marginBottom:3 }}>Session Status</MonoLabel>
                              <select value={editLog.session_status} onChange={e=>setEditLog(x=>({...x,session_status:e.target.value}))} style={inp}>
                                {Object.entries(SESSION_COLOR).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
                              </select>
                            </div>
                            <div>
                              <MonoLabel style={{ marginBottom:3 }}>Notes</MonoLabel>
                              <input value={editLog.notes} onChange={e=>setEditLog(x=>({...x,notes:e.target.value}))} style={inp}/>
                            </div>
                            <div style={{ display:'flex', flexDirection:'column', justifyContent:'flex-end', gap:6 }}>
                              <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:12.5 }}>
                                <input type="checkbox" checked={editLog.admin_override} onChange={e=>setEditLog(x=>({...x,admin_override:e.target.checked}))}/>
                                Admin Override
                              </label>
                              {editLog.admin_override && <input value={editLog.admin_override_reason} onChange={e=>setEditLog(x=>({...x,admin_override_reason:e.target.value}))} placeholder="Reason" style={inp}/>}
                            </div>
                          </div>
                          <div style={{ display:'flex', gap:6 }}>
                            <Btn variant="primary" small onClick={()=>saveLogEntry(h.id)}>Save</Btn>
                            <Btn small onClick={()=>setEditLogId(null)}>Cancel</Btn>
                          </div>
                        </div>
                      )}
                    </Card>
                  );
                })}
              {hoursLog.filter(h=>(logTeacher==='all'||h.teacher_id===logTeacher)&&(logStatus==='all'||h.session_status===logStatus)).length === 0 && (
                <div style={{ color:'#9aa3b8', textAlign:'center', padding:'24px 0', fontSize:13 }}>No sessions logged for this period.</div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── SECTION D: ADJUSTMENTS ── */}
      {section === 'adjustments' && (
        <div>
          <SectionHeader title="Manual Adjustments"/>

          <Card style={{ marginBottom:16, border:`1px solid ${T.gold}44` }}>
            <div style={{ fontSize:13.5, fontWeight:600, color:T.gold, marginBottom:12 }}>Add Adjustment</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:10, marginBottom:12 }}>
              <div>
                <MonoLabel style={{ marginBottom:4 }}>Teacher</MonoLabel>
                <select value={adjTeacher} onChange={e=>setAdjTeacher(e.target.value)} style={inp}>
                  <option value="">Select teacher</option>
                  {teachers.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <MonoLabel style={{ marginBottom:4 }}>Type</MonoLabel>
                <select value={adjType} onChange={e=>setAdjType(e.target.value)} style={inp}>
                  <option value="bonus">Bonus (+amount)</option>
                  <option value="deduction">Deduction (-amount)</option>
                  <option value="hour_add">Add Hours</option>
                  <option value="hour_subtract">Subtract Hours</option>
                </select>
              </div>
              {['bonus','deduction'].includes(adjType) && (
                <div>
                  <MonoLabel style={{ marginBottom:4 }}>Amount ({cfgFor(adjTeacher)?.currency||'AED'})</MonoLabel>
                  <input type="number" value={adjAmount} onChange={e=>setAdjAmount(e.target.value)} placeholder="0.00" style={inp}/>
                </div>
              )}
              {['hour_add','hour_subtract'].includes(adjType) && (
                <div>
                  <MonoLabel style={{ marginBottom:4 }}>Hours</MonoLabel>
                  <input type="number" step="0.5" value={adjHours} onChange={e=>setAdjHours(e.target.value)} placeholder="0.0" style={inp}/>
                </div>
              )}
              <div>
                <MonoLabel style={{ marginBottom:4 }}>Month / Year</MonoLabel>
                <div style={{ display:'flex', gap:4 }}>
                  <select value={adjMonth} onChange={e=>setAdjMonth(Number(e.target.value))} style={{ ...inp, flex:1 }}>
                    {MONTHS.map((m,i)=><option key={i} value={i+1}>{m.slice(0,3)}</option>)}
                  </select>
                  <input type="number" value={adjYear} onChange={e=>setAdjYear(Number(e.target.value))} style={{ ...inp, width:72 }}/>
                </div>
              </div>
              <div style={{ gridColumn:'1 / -1' }}>
                <MonoLabel style={{ marginBottom:4 }}>Reason (required)</MonoLabel>
                <input value={adjReason} onChange={e=>setAdjReason(e.target.value)} placeholder="e.g. End-of-month bonus, late cancellation deduction..." style={inp}/>
              </div>
            </div>
            <Btn variant="primary" small onClick={saveAdjustment} disabled={!adjTeacher||!adjReason}>Save Adjustment</Btn>
          </Card>

          {/* Audit log */}
          <div style={{ fontSize:13.5, fontWeight:600, color:'#1a2233', marginBottom:10 }}>Adjustment History</div>
          {adjustments.length === 0 && <div style={{ color:'#9aa3b8', fontSize:13, padding:'16px 0' }}>No adjustments yet.</div>}
          {adjustments.map(a => (
            <Card key={a.id} style={{ marginBottom:7, padding:'11px 15px' }}>
              <div style={{ display:'flex', gap:10, alignItems:'flex-start', flexWrap:'wrap' }}>
                <div style={{ flex:1, minWidth:180 }}>
                  <div style={{ fontWeight:600, fontSize:13, color:'#1a2233', marginBottom:2 }}>{a.teachers?.name||'—'}</div>
                  <div style={{ fontSize:12.5, color:'#5a6a88' }}>{a.reason}</div>
                </div>
                <div style={{ textAlign:'right', flexShrink:0 }}>
                  <div style={{ fontWeight:700, fontSize:13.5, color: a.adjustment_type==='deduction'?'#c0392b': a.adjustment_type==='bonus'?'#1a7a4a':'#1a5fa8' }}>
                    {a.adjustment_type==='bonus'?'+':a.adjustment_type==='deduction'?'-':''}
                    {a.amount>0 ? ` ${a.amount} AED` : ''}
                    {a.hours>0  ? ` ${a.hours}h` : ''}
                  </div>
                  <div style={{ fontSize:11, color:'#9aa3b8', marginTop:2 }}>
                    <span style={{ fontSize:11, fontWeight:600, padding:'1px 7px', borderRadius:100, background:`${PAY_TYPE_COLORS.hourly}18`, color:PAY_TYPE_COLORS.hourly, border:`1px solid ${PAY_TYPE_COLORS.hourly}33` }}>{a.adjustment_type}</span>
                    {' · '}{fmtDate(a.created_at)}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* ── SECTION E: POLICY EDITOR ── */}
      {section === 'policy' && (
        <div>
          <SectionHeader title="Cancellation Policy"
            action={<Btn small onClick={copyPolicy}>📋 Copy for WhatsApp</Btn>}/>

          {policies.map(p => {
            const isEditing = editPolicyId === p.id;
            return (
              <Card key={p.id} style={{ marginBottom:10, opacity: p.active ? 1 : 0.55 }}>
                <div style={{ display:'flex', gap:10, alignItems:'flex-start', flexWrap:'wrap' }}>
                  <div style={{ flex:1 }}>
                    {!isEditing ? (
                      <>
                        <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:5 }}>
                          <div style={{ fontWeight:700, fontSize:13.5, color:'#1a2233' }}>{p.rule_title}</div>
                          <span style={{ fontSize:10.5, padding:'1px 8px', borderRadius:100, color:p.applies_to==='student'?'#1a5fa8':'#b8620a', background:p.applies_to==='student'?'#e8f0fb':'#fff8f0', border:`1px solid ${p.applies_to==='student'?'#c0d8f8':'#f0d890'}` }}>{p.applies_to}</span>
                          {!p.active && <span style={{ fontSize:10.5, color:'#9aa3b8' }}>inactive</span>}
                        </div>
                        <div style={{ fontSize:13, color:'#5a6a88', lineHeight:1.65 }}>{p.rule_description}</div>
                      </>
                    ) : (
                      <div>
                        <MonoLabel style={{ marginBottom:4 }}>Title</MonoLabel>
                        <input value={policyForm.rule_title||''} onChange={e=>setPolicyForm(f=>({...f,rule_title:e.target.value}))} style={{ ...acBase.inp, marginBottom:10 }}/>
                        <MonoLabel style={{ marginBottom:4 }}>Description</MonoLabel>
                        <textarea value={policyForm.rule_description||''} onChange={e=>setPolicyForm(f=>({...f,rule_description:e.target.value}))} rows={3}
                          style={{ ...acBase.inp, resize:'vertical', marginBottom:10 }}/>
                        <div style={{ display:'flex', gap:8 }}>
                          <Btn variant="primary" small onClick={()=>savePolicy(p.id)}>Save</Btn>
                          <Btn small onClick={()=>setEditPolicyId(null)}>Cancel</Btn>
                        </div>
                      </div>
                    )}
                  </div>
                  {!isEditing && (
                    <div style={{ display:'flex', gap:5, flexShrink:0 }}>
                      <Btn small onClick={()=>{ setEditPolicyId(p.id); setPolicyForm({ rule_title:p.rule_title, rule_description:p.rule_description }); }}>Edit</Btn>
                      <Btn small onClick={async()=>{ await supabase.from('cancellation_policy').update({ active:!p.active }).eq('id',p.id); fetchPolicies(); }}>
                        {p.active ? 'Deactivate' : 'Activate'}
                      </Btn>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}

          {/* WhatsApp preview */}
          <div style={{ marginTop:16, background:'#f0faf4', border:'1px solid #c8ead8', borderRadius:10, padding:'16px 18px' }}>
            <div style={{ fontSize:12, fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.12em', textTransform:'uppercase', color:'#1a7a4a', marginBottom:8 }}>WhatsApp Preview</div>
            <pre style={{ fontSize:12, color:'#2a3a2a', lineHeight:1.8, whiteSpace:'pre-wrap', fontFamily:"'Plus Jakarta Sans',sans-serif", margin:0 }}>
{`📋 COMPASS ACADEMY — CANCELLATION POLICY\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n${policies.filter(p=>p.active).map(p=>`*${p.rule_title}*\n${p.rule_description}`).join('\n\n')}\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\nFor queries contact Mr. Ahmed Salem\nwa.me/${ADMIN_WA}`}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
